import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Card, CardBody } from "@/components/ui/Card";
import {
  AnchoredDetailCalloutBody,
  AnchoredDetailCalloutFieldLabel,
  AnchoredDetailCalloutFooter,
  AnchoredDetailCalloutTitle,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX,
  DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX,
  FormAnchoredDetailCallout,
} from "@/components/ui/AnchoredDetailFormCallout";
import { buildAttributeAnchorSelector } from "@/components/ui/AnchoredDetailCallout";
import { CardTitleWithHelp, HoverHintContent } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { HUB_CACHE_CHANNEL } from "@/lib/hubCacheChannels";
import { useHubCacheStream } from "@/lib/useHubCacheStream";
import { fetchQuickBooksTimeOAuthStatus, startQuickBooksTimeOAuth } from "@/lib/quickbooksTimeOAuth";
import {
  friendlyDailyTaskLoadError,
  friendlyQuickBooksTimeLoadError,
  quickBooksTimeNeedsReconnect,
} from "@/lib/quickBooksTimeMessages";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import {
  MANUFACTURING_REPORTING_STATION_COLORS,
  manufacturingReportingStationActiveBlockClass,
  manufacturingReportingStationBlockClass,
  manufacturingReportingStationKeyFromLaborLabel,
} from "@/pages/manufacturing/manufacturingReportingStationColors";
import { cn } from "@/lib/utils";

const LABOR_TIME_ZONE = "America/Chicago";
const DEFAULT_TIMELINE_START_HOUR = 6;
const DEFAULT_TIMELINE_END_HOUR = 18;
const SNAP_MINUTES = 15;
const CLICK_DURATION_MS = 30 * 60 * 1000;
const MIN_DRAG_DURATION_MS = 15 * 60 * 1000;
const MIN_DRAG_DISTANCE_PX = 4;
const ACTIVE_BLOCK_BLEED_MS = 60 * 60 * 1000;

const DAILY_TASK_MISC_ANCHOR_ATTRIBUTE = "data-daily-task-misc-anchor";
const DAILY_TASK_BLOCK_ATTRIBUTE = "data-daily-task-block";
const DAILY_TASK_MISC_BLOCK_ATTRIBUTE = "data-daily-task-misc-block";

const QUICKBOOKS_TIME_OFF_CLOCK_HATCH_CLASS =
  "border border-slate-300 bg-[repeating-linear-gradient(-45deg,#cbd5e1,#cbd5e1_3px,#e2e8f0_3px,#e2e8f0_6px)]";
const QUICKBOOKS_TIME_BREAK_HATCH_CLASS =
  "border border-slate-400 bg-[repeating-linear-gradient(-45deg,#e2e8f0,#e2e8f0_3px,#f8fafc_3px,#f8fafc_6px)]";

/** Set during {@link EmployeeTimelineRow} render on today's report for QuickBooks Time hover hints. */
let dailyTaskHintNowMs: number | undefined;

interface DailyTaskBlock {
  kind: "device" | "task" | "misc";
  id: number;
  startedAt: string;
  endedAt: string;
  label: string;
  deviceCount: number | null;
  active: boolean;
  createdByUserId: string | null;
  createdByLabel: string | null;
}

interface DailyTaskEmployee {
  key: string;
  label: string;
  userId: string;
  blocks: DailyTaskBlock[];
  quickbooksTime: DailyTaskQuickBooksTimeSummary | null;
}

interface DailyTaskQuickBooksTimeBreak {
  startedAt: string;
  endedAt: string;
  label: string;
  paid: boolean;
}

interface DailyTaskQuickBooksTimeOffClockSpan {
  startedAt: string;
  endedAt: string;
}

interface DailyTaskQuickBooksTimeSummary {
  matched: boolean;
  quickbooksTimeUserId: number | null;
  onTheClock: boolean;
  clockIn: string | null;
  clockOut: string | null;
  breaks: DailyTaskQuickBooksTimeBreak[];
  offClockSpans: DailyTaskQuickBooksTimeOffClockSpan[];
}

type DailyTaskQuickBooksTimeStatus = "not_configured" | "ok" | "error";

interface DailyTaskReportResponse {
  date: string;
  timeZone: string;
  dayStart: string;
  dayEnd: string;
  employees: DailyTaskEmployee[];
  quickbooksTimeStatus: DailyTaskQuickBooksTimeStatus;
  quickbooksTimeError: string | null;
  cached_at?: string;
  stale?: boolean;
  revalidating?: boolean;
  next_refresh_after?: string | null;
}

interface TimelineWindow {
  windowStartMs: number;
  windowEndMs: number;
  spanMs: number;
  startHour: number;
  endHour: number;
}

interface MiscCalloutCreateState {
  mode: "create";
  anchorKey: string;
  userId: string;
  employeeLabel: string;
  startTime: string;
  endTime: string;
  leftPct: number;
  widthPct: number;
}

interface MiscCalloutEditState {
  mode: "edit";
  miscEntryId: number;
  userId: string;
  employeeLabel: string;
  startTime: string;
  endTime: string;
  description: string;
  createdByLabel: string | null;
}

type MiscCalloutState = MiscCalloutCreateState | MiscCalloutEditState;

interface ActiveLaneDrag {
  userId: string;
  employeeLabel: string;
  pointerId: number;
  startMs: number;
  currentMs: number;
  startClientX: number;
  moved: boolean;
}

function blockColorClass(block: DailyTaskBlock): string {
  const stationKey = manufacturingReportingStationKeyFromLaborLabel(block.label, block.kind);
  if (block.active) {
    return manufacturingReportingStationActiveBlockClass(stationKey);
  }
  return manufacturingReportingStationBlockClass(stationKey);
}

function formatStationPage(page: string): string {
  const trimmed = page.trim();
  if (trimmed.includes("/")) {
    const slashIndex = trimmed.indexOf("/");
    const stationPage = trimmed.slice(0, slashIndex);
    const jobKey = trimmed.slice(slashIndex + 1);
    return `${formatStationPage(stationPage)} / ${formatReceivingJobKeyLabel(jobKey)}`;
  }
  if (trimmed === "cal+test") {
    return "Cal + test";
  }
  if (trimmed === "Orders") {
    return "Orders (misc)";
  }
  if (trimmed === "") {
    return "—";
  }
  return trimmed
    .split("-")
    .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(" ");
}

function formatReceivingJobKeyLabel(jobKey: string): string {
  const trimmed = jobKey.trim();
  if (trimmed === "arrivals") return "Arrivals";
  if (trimmed === "holding") return "Holding";
  if (trimmed === "to-stock") return "To stock";
  if (trimmed === "receive") return "Receive";
  if (trimmed.startsWith("receive:purchase_order:")) {
    const id = trimmed.slice("receive:purchase_order:".length);
    return id === "" ? "Receive PO" : `Receive PO ${id}`;
  }
  if (trimmed.startsWith("receive:transfer_order:")) {
    const id = trimmed.slice("receive:transfer_order:".length);
    return id === "" ? "Receive transfer" : `Receive transfer ${id}`;
  }
  return trimmed;
}

function formatBlockLabel(block: DailyTaskBlock): string {
  if (block.kind === "misc") {
    return block.label;
  }
  return formatStationPage(block.label);
}

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  timeZone: LABOR_TIME_ZONE,
  hour: "numeric",
  minute: "2-digit",
});

const time24Formatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: LABOR_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const dateTitleFormatter = new Intl.DateTimeFormat(undefined, {
  timeZone: LABOR_TIME_ZONE,
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});

function formatChicagoTime(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return timeFormatter.format(parsed);
}

function msToChicagoTimeString(ms: number): string {
  return time24Formatter.format(new Date(ms));
}

function shiftCalendarDate(date: string, deltaDays: number): string {
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return date;
  }
  parsed.setDate(parsed.getDate() + deltaDays);
  return parsed.toISOString().slice(0, 10);
}

function computeTimelineHours(
  employees: DailyTaskEmployee[],
  dayStartIso: string,
  options?: { includeNowMs?: number },
): { startHour: number; endHour: number } {
  let startHour = DEFAULT_TIMELINE_START_HOUR;
  let endHour = DEFAULT_TIMELINE_END_HOUR;
  const dayStartMs = new Date(dayStartIso).getTime();

  const considerMs = (ms: number) => {
    const hourFloat = (ms - dayStartMs) / (3600 * 1000);
    if (hourFloat < DEFAULT_TIMELINE_START_HOUR) {
      startHour = Math.min(startHour, Math.floor(hourFloat));
    }
    if (hourFloat > DEFAULT_TIMELINE_END_HOUR) {
      endHour = Math.max(endHour, Math.ceil(hourFloat));
    }
  };

  if (options?.includeNowMs != null) {
    considerMs(options.includeNowMs);
  }

  for (const employee of employees) {
    for (const block of employee.blocks) {
      considerMs(new Date(block.startedAt).getTime());
      considerMs(new Date(block.endedAt).getTime());
      if (block.active && options?.includeNowMs != null) {
        considerMs(options.includeNowMs + ACTIVE_BLOCK_BLEED_MS);
      }
    }
    const quickbooksTime = employee.quickbooksTime;
    if (quickbooksTime == null) {
      continue;
    }
    if (quickbooksTime.clockIn != null) {
      considerMs(new Date(quickbooksTime.clockIn).getTime());
    }
    if (quickbooksTime.clockOut != null) {
      considerMs(new Date(quickbooksTime.clockOut).getTime());
    }
    for (const breakEntry of quickbooksTime.breaks) {
      considerMs(new Date(breakEntry.startedAt).getTime());
      considerMs(new Date(breakEntry.endedAt).getTime());
    }
  }

  return { startHour, endHour };
}

function timelineWindowFromHours(dayStartIso: string, startHour: number, endHour: number): TimelineWindow {
  const dayStartMs = new Date(dayStartIso).getTime();
  const windowStartMs = dayStartMs + startHour * 60 * 60 * 1000;
  const windowEndMs = dayStartMs + endHour * 60 * 60 * 1000;
  return {
    windowStartMs,
    windowEndMs,
    spanMs: windowEndMs - windowStartMs,
    startHour,
    endHour,
  };
}

function snapMs(ms: number, dayStartMs: number): number {
  const offsetMinutes = (ms - dayStartMs) / 60000;
  const snappedMinutes = Math.round(offsetMinutes / SNAP_MINUTES) * SNAP_MINUTES;
  return dayStartMs + snappedMinutes * 60000;
}

function pointerXToMs(clientX: number, laneRect: DOMRect, window: TimelineWindow, dayStartMs: number): number {
  const ratio = (clientX - laneRect.left) / laneRect.width;
  const clamped = Math.max(0, Math.min(1, ratio));
  const rawMs = window.windowStartMs + clamped * window.spanMs;
  return snapMs(rawMs, dayStartMs);
}

function blockPosition(
  block: DailyTaskBlock,
  window: TimelineWindow,
  options?: { activeBleedEndMs?: number },
): { leftPct: number; widthPct: number } | null {
  const startMs = new Date(block.startedAt).getTime();
  let endMs = new Date(block.endedAt).getTime();
  if (block.active && options?.activeBleedEndMs != null) {
    endMs = Math.max(endMs, options.activeBleedEndMs);
  }
  const clipStart = Math.max(startMs, window.windowStartMs);
  const clipEnd = Math.min(endMs, window.windowEndMs);
  if (clipEnd <= clipStart || window.spanMs <= 0) {
    return null;
  }
  const leftPct = ((clipStart - window.windowStartMs) / window.spanMs) * 100;
  const widthPct = ((clipEnd - clipStart) / window.spanMs) * 100;
  return { leftPct, widthPct: Math.max(widthPct, 0.4) };
}

function rangePosition(startMs: number, endMs: number, window: TimelineWindow): { leftPct: number; widthPct: number } {
  const clipStart = Math.max(startMs, window.windowStartMs);
  const clipEnd = Math.min(endMs, window.windowEndMs);
  const leftPct = ((clipStart - window.windowStartMs) / window.spanMs) * 100;
  const widthPct = ((clipEnd - clipStart) / window.spanMs) * 100;
  return { leftPct, widthPct: Math.max(widthPct, 0.8) };
}

function clockMarkerPosition(iso: string, window: TimelineWindow): number | null {
  return msPositionOnTimeline(new Date(iso).getTime(), window);
}

function msPositionOnTimeline(ms: number, window: TimelineWindow): number | null {
  if (Number.isNaN(ms) || window.spanMs <= 0) {
    return null;
  }
  if (ms < window.windowStartMs || ms > window.windowEndMs) {
    return null;
  }
  return ((ms - window.windowStartMs) / window.spanMs) * 100;
}

function formatSpanEndLabel(endIso: string, nowMs?: number): string {
  if (nowMs == null) {
    return formatChicagoTime(endIso);
  }
  const endMs = new Date(endIso).getTime();
  if (endMs >= nowMs - 60_000) {
    return "ongoing.";
  }
  return formatChicagoTime(endIso);
}

function quickBooksTimeBreakHint(breakEntry: DailyTaskQuickBooksTimeBreak): string {
  if (dailyTaskHintNowMs != null) {
    const label = breakEntry.label.trim() || "Break";
    return `${label}: ${formatChicagoTime(breakEntry.startedAt)} - ${formatSpanEndLabel(breakEntry.endedAt, dailyTaskHintNowMs)}`;
  }
  const paidLabel = breakEntry.paid ? "Paid break" : "Unpaid break";
  return `${breakEntry.label}\n${formatChicagoTime(breakEntry.startedAt)} – ${formatChicagoTime(breakEntry.endedAt)}\n${paidLabel} (QuickBooks Time)`;
}

function quickBooksTimeOffClockHint(span: DailyTaskQuickBooksTimeOffClockSpan): string {
  if (dailyTaskHintNowMs != null) {
    return `Clocked out: ${formatChicagoTime(span.startedAt)} - ${formatSpanEndLabel(span.endedAt, dailyTaskHintNowMs)}`;
  }
  return `Off the clock (QuickBooks Time)\n${formatChicagoTime(span.startedAt)} – ${formatChicagoTime(span.endedAt)}`;
}

function quickBooksTimeClockHint(kind: "in" | "out", iso: string, onTheClock: boolean): string {
  if (kind === "out" && onTheClock) {
    return `QuickBooks Time — still clocked in at ${formatChicagoTime(iso)}`;
  }
  const verb = kind === "in" ? "Clock in" : "Clock out";
  return `QuickBooks Time — ${verb} ${formatChicagoTime(iso)}`;
}

function TimelineCurrentTimeMarker({
  window,
  nowMs,
}: {
  window: TimelineWindow;
  nowMs: number;
}): JSX.Element | null {
  const leftPct = msPositionOnTimeline(nowMs, window);
  if (leftPct == null) {
    return null;
  }
  return (
    <div
      className="pointer-events-none absolute inset-y-0 z-[4] w-0.5 -translate-x-1/2 bg-sky-600/90"
      style={{ left: `${leftPct}%` }}
      aria-hidden
    >
      <span className="absolute -top-5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-sky-700 px-1 py-0.5 text-[9px] font-medium tabular-nums text-white">
        Now
      </span>
    </div>
  );
}

function blockHint(block: DailyTaskBlock): string {
  const station = formatBlockLabel(block);
  const range = `${formatChicagoTime(block.startedAt)} – ${formatChicagoTime(block.endedAt)}`;
  if (block.kind === "misc") {
    const addedBy = block.createdByLabel?.trim() || "Unknown";
    return `${station}\n${range}\nAdded by ${addedBy}`;
  }
  const scans =
    block.deviceCount == null ? "No scan count" : `${block.deviceCount.toLocaleString()} scans`;
  const activeNote = block.active ? "\nStill clocked in" : "";
  return `${station}\n${range}\n${scans}${activeNote}`;
}

function miscAnchorSelector(anchorKey: string): string {
  return buildAttributeAnchorSelector(DAILY_TASK_MISC_ANCHOR_ATTRIBUTE, anchorKey);
}

function miscBlockAnchorSelector(miscEntryId: number): string {
  return buildAttributeAnchorSelector(DAILY_TASK_MISC_BLOCK_ATTRIBUTE, String(miscEntryId));
}

function miscCalloutAnchorSelector(state: MiscCalloutState): string {
  if (state.mode === "edit") {
    return miscBlockAnchorSelector(state.miscEntryId);
  }
  return miscAnchorSelector(state.anchorKey);
}

function TimelineHourLabels({ dayStartIso, window }: { dayStartIso: string; window: TimelineWindow }) {
  const labels = useMemo(() => {
    const dayStartMs = new Date(dayStartIso).getTime();
    const items: string[] = [];
    const step = 2;
    for (let hour = window.startHour; hour <= window.endHour; hour += step) {
      const tick = new Date(dayStartMs + hour * 60 * 60 * 1000);
      items.push(timeFormatter.format(tick));
    }
    if (items.length === 0 || window.endHour % step !== 0) {
      const endTick = new Date(dayStartMs + window.endHour * 60 * 60 * 1000);
      const endLabel = timeFormatter.format(endTick);
      if (items[items.length - 1] !== endLabel) {
        items.push(endLabel);
      }
    }
    return items;
  }, [dayStartIso, window.endHour, window.startHour]);

  return (
    <div className="flex items-stretch gap-3 border-b border-slate-200 pb-1">
      <div className="w-24 shrink-0" aria-hidden />
      <div className="relative h-6 min-w-0 flex-1">
        {labels.map((label, index) => (
          <span
            key={`${label}-${index}`}
            className="absolute -translate-x-1/2 text-[10px] tabular-nums text-slate-500"
            style={{ left: `${(index / (labels.length - 1)) * 100}%` }}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

function StationColorLegend(): JSX.Element {
  return (
    <div className="space-y-2 border-t border-slate-200 pt-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
        {MANUFACTURING_REPORTING_STATION_COLORS.map((station) => (
          <span key={station.key} className="inline-flex items-center gap-1.5">
            <span className={cn("h-2.5 w-2.5 rounded-sm border", station.block)} />
            {station.label}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("h-3 w-6 rounded-sm", QUICKBOOKS_TIME_OFF_CLOCK_HATCH_CLASS)} />
          Off the clock (QuickBooks Time)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("h-3 w-6 rounded-sm", QUICKBOOKS_TIME_BREAK_HATCH_CLASS)} />
          Break (QuickBooks Time)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm border border-emerald-700 bg-emerald-500" />
          QuickBooks Time clock in
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm border border-rose-700 bg-rose-500" />
          QuickBooks Time clock out
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-6 rounded-sm border-l-2 border-lime-700 bg-gradient-to-r from-lime-600 to-transparent" />
          Currently at station (ongoing)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-0.5 bg-sky-600" />
          Current time
        </span>
      </div>
    </div>
  );
}

function MiscEntryCallout({
  state,
  date,
  employees,
  onClose,
  onSaved,
}: {
  state: MiscCalloutState;
  date: string;
  employees: DailyTaskEmployee[];
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [userId, setUserId] = useState(state.userId);
  const [startTime, setStartTime] = useState(state.startTime);
  const [endTime, setEndTime] = useState(state.endTime);
  const [description, setDescription] = useState(state.mode === "edit" ? state.description : "");
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        userId,
        date,
        startTime,
        endTime,
        description,
      };
      if (state.mode === "create") {
        return apiFetch<{ block: DailyTaskBlock }>("/api/manufacturing/reporting/daily-task/misc-entry", {
          method: "POST",
          body,
        });
      }
      return apiFetch<{ block: DailyTaskBlock }>(
        `/api/manufacturing/reporting/daily-task/misc-entry/${String(state.miscEntryId)}`,
        {
          method: "PATCH",
          body,
        },
      );
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const payload = err.body as { error?: string } | undefined;
        setError(payload?.error ?? err.message);
        return;
      }
      setError(state.mode === "create" ? "Failed to add misc time entry." : "Failed to update misc time entry.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (state.mode !== "edit") {
        return;
      }
      await apiFetch(`/api/manufacturing/reporting/daily-task/misc-entry/${String(state.miscEntryId)}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const payload = err.body as { error?: string } | undefined;
        setError(payload?.error ?? err.message);
        return;
      }
      setError("Failed to delete misc time entry.");
    },
  });

  const employeeLabel =
    employees.find((employee) => employee.userId === userId)?.label ?? state.employeeLabel;
  const title =
    state.mode === "create" ? `Add misc time — ${employeeLabel}` : `Edit misc time — ${employeeLabel}`;

  return (
    <FormAnchoredDetailCallout
      anchorSelector={miscCalloutAnchorSelector(state)}
      onClose={onClose}
      panelWidth={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_WIDTH_PX}
      minContentHeight={DEFAULT_FORM_ANCHORED_DETAIL_CALLOUT_MIN_HEIGHT_PX}
      ariaLabel={title}
      measureKey={`${state.mode}-${state.mode === "edit" ? state.miscEntryId : state.anchorKey}-${startTime}-${endTime}`}
    >
      <AnchoredDetailCalloutBody>
        <AnchoredDetailCalloutTitle>{title}</AnchoredDetailCalloutTitle>
        <p className="text-xs text-slate-500">
          Times are local to {LABOR_TIME_ZONE} on the selected day.
          {state.mode === "edit" && state.createdByLabel != null
            ? ` Added by ${state.createdByLabel}.`
            : null}
        </p>
        <AnchoredDetailCalloutFieldLabel label="Team member">
          <select
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            {employees.map((employee) => (
              <option key={employee.userId} value={employee.userId}>
                {employee.label}
              </option>
            ))}
          </select>
        </AnchoredDetailCalloutFieldLabel>
        <div className="grid gap-3 sm:grid-cols-2">
          <AnchoredDetailCalloutFieldLabel label="Start time">
            <input
              type="time"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
          </AnchoredDetailCalloutFieldLabel>
          <AnchoredDetailCalloutFieldLabel label="End time">
            <input
              type="time"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </AnchoredDetailCalloutFieldLabel>
        </div>
        <AnchoredDetailCalloutFieldLabel label="Description">
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="What they were doing"
          />
        </AnchoredDetailCalloutFieldLabel>
        {error != null ? <p className="text-sm text-red-600">{error}</p> : null}
      </AnchoredDetailCalloutBody>
      <AnchoredDetailCalloutFooter className="justify-between">
        {state.mode === "edit" ? (
          <Button
            type="button"
            variant="danger"
            disabled={deleteMutation.isPending || saveMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <SaveChangesButton
            type="button"
            isDirty={
              state.mode === "create"
                ? description.trim() !== ""
                : userId !== state.userId ||
                  startTime !== state.startTime ||
                  endTime !== state.endTime ||
                  description.trim() !== state.description.trim()
            }
            disabled={saveMutation.isPending || deleteMutation.isPending || description.trim() === ""}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? "Saving…" : "Save"}
          </SaveChangesButton>
        </div>
      </AnchoredDetailCalloutFooter>
    </FormAnchoredDetailCallout>
  );
}

function EmployeeTimelineRow({
  employee,
  window,
  hubWritesAllowed,
  miscCallout,
  dragPreview,
  nowMs,
  viewingToday,
  onLanePointerDown,
  onLanePointerMove,
  onLanePointerUp,
  onMiscBlockClick,
}: {
  employee: DailyTaskEmployee;
  window: TimelineWindow;
  hubWritesAllowed: boolean;
  miscCallout: MiscCalloutState | null;
  dragPreview: { userId: string; startMs: number; endMs: number } | null;
  nowMs: number;
  viewingToday: boolean;
  onLanePointerDown: (event: ReactPointerEvent<HTMLDivElement>, employee: DailyTaskEmployee) => void;
  onLanePointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onLanePointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onMiscBlockClick: (block: DailyTaskBlock, employee: DailyTaskEmployee) => void;
}) {
  dailyTaskHintNowMs = viewingToday ? nowMs : undefined;
  const activeBleedEndMs = viewingToday ? Math.min(nowMs + ACTIVE_BLOCK_BLEED_MS, window.windowEndMs) : undefined;

  const gridLineCount = Math.max(1, window.endHour - window.startHour);

  const createPreview =
    miscCallout?.mode === "create" && miscCallout.userId === employee.userId
      ? { leftPct: miscCallout.leftPct, widthPct: miscCallout.widthPct, anchorKey: miscCallout.anchorKey }
      : null;

  const preview =
    dragPreview?.userId === employee.userId
      ? rangePosition(dragPreview.startMs, dragPreview.endMs, window)
      : createPreview;

  const quickbooksTime = employee.quickbooksTime;
  const clockInLeftPct =
    quickbooksTime?.clockIn != null ? clockMarkerPosition(quickbooksTime.clockIn, window) : null;
  const clockOutLeftPct =
    quickbooksTime?.clockOut != null ? clockMarkerPosition(quickbooksTime.clockOut, window) : null;

  return (
    <div className="flex items-stretch gap-3 py-2">
      <div className="w-24 shrink-0 pt-1 text-sm font-medium text-slate-800">{employee.label}</div>
      <div
        className={cn(
          "relative min-h-11 flex-1 rounded-md border border-slate-200 bg-slate-50",
          hubWritesAllowed && "touch-none",
        )}
        onPointerDown={(event) => {
          if (!hubWritesAllowed) return;
          if ((event.target as HTMLElement).closest(`[${DAILY_TASK_BLOCK_ATTRIBUTE}]`) != null) return;
          onLanePointerDown(event, employee);
        }}
        onPointerMove={onLanePointerMove}
        onPointerUp={onLanePointerUp}
      >
        {Array.from({ length: gridLineCount + 1 }).map((_, index) => (
          <div
            key={index}
            className="pointer-events-none absolute inset-y-0 border-l border-slate-200/80"
            style={{ left: `${(index / gridLineCount) * 100}%` }}
          />
        ))}
        {preview != null ? (
          <div
            {...(createPreview != null ? { [DAILY_TASK_MISC_ANCHOR_ATTRIBUTE]: createPreview.anchorKey } : {})}
            className="pointer-events-none absolute top-1 bottom-1 rounded border-2 border-amber-600 bg-amber-200/50"
            style={{ left: `${preview.leftPct}%`, width: `${preview.widthPct}%` }}
          />
        ) : null}
        {quickbooksTime?.offClockSpans.map((offClockSpan, index) => {
          const position = blockPosition(
            {
              kind: "misc",
              id: index,
              startedAt: offClockSpan.startedAt,
              endedAt: offClockSpan.endedAt,
              label: "Off the clock",
              deviceCount: null,
              active: false,
              createdByUserId: null,
              createdByLabel: null,
            },
            window,
          );
          if (position == null) {
            return null;
          }
          return (
            <div
              key={`qbt-off-clock-${index}`}
              className="absolute inset-y-1 z-0 min-w-[2px]"
              style={{
                left: `${position.leftPct}%`,
                width: `${position.widthPct}%`,
              }}
            >
              <HoverHintContent
                hoverHint={quickBooksTimeOffClockHint(offClockSpan)}
                block
                cursor="default"
                className={cn("h-full w-full rounded", QUICKBOOKS_TIME_OFF_CLOCK_HATCH_CLASS)}
              >
                <span className="sr-only">Off the clock</span>
              </HoverHintContent>
            </div>
          );
        })}
        {quickbooksTime?.breaks.map((breakEntry, index) => {
          const position = blockPosition(
            {
              kind: "misc",
              id: index,
              startedAt: breakEntry.startedAt,
              endedAt: breakEntry.endedAt,
              label: breakEntry.label,
              deviceCount: null,
              active: false,
              createdByUserId: null,
              createdByLabel: null,
            },
            window,
          );
          if (position == null) {
            return null;
          }
          return (
            <div
              key={`qbt-break-${index}`}
              className="absolute top-1 bottom-1 z-0 min-w-[2px]"
              style={{
                left: `${position.leftPct}%`,
                width: `${position.widthPct}%`,
              }}
            >
              <HoverHintContent
                hoverHint={quickBooksTimeBreakHint(breakEntry)}
                block
                cursor="default"
                className={cn("h-full w-full rounded", QUICKBOOKS_TIME_BREAK_HATCH_CLASS)}
              >
                <span className="sr-only">{breakEntry.label}</span>
              </HoverHintContent>
            </div>
          );
        })}
        <div className="hidden" aria-hidden>
        {clockInLeftPct != null && quickbooksTime?.clockIn != null ? (
          <div
            className="pointer-events-none absolute inset-y-0 z-[2] w-0.5 -translate-x-1/2 bg-emerald-600"
            style={{ left: `${clockInLeftPct}%` }}
          >
            <HoverHintContent
              hoverHint={quickBooksTimeClockHint("in", quickbooksTime.clockIn, quickbooksTime.onTheClock)}
              block
              cursor="default"
              className="relative block h-full w-full"
            >
              <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full border border-emerald-800 bg-emerald-500" />
            </HoverHintContent>
          </div>
        ) : null}
        {clockOutLeftPct != null && quickbooksTime?.clockOut != null ? (
          <div
            className="pointer-events-none absolute inset-y-0 z-[2] w-0.5 -translate-x-1/2 bg-rose-600"
            style={{ left: `${clockOutLeftPct}%` }}
          >
            <HoverHintContent
              hoverHint={quickBooksTimeClockHint("out", quickbooksTime.clockOut, quickbooksTime.onTheClock)}
              block
              cursor="default"
              className="relative block h-full w-full"
            >
              <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rounded-full border border-rose-800 bg-rose-500" />
            </HoverHintContent>
          </div>
        ) : null}
        </div>
        {quickbooksTime?.onTheClock && quickbooksTime.clockIn != null && clockOutLeftPct == null ? (
          <HoverHintContent
            hoverHint={quickBooksTimeClockHint("in", quickbooksTime.clockIn, true)}
            block
            cursor="default"
            className="pointer-events-none absolute inset-y-1 right-1 z-[2]"
          >
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-800">
              Clocked in
            </span>
          </HoverHintContent>
        ) : null}
        {employee.blocks.map((block) => {
          const position = blockPosition(block, window, {
            activeBleedEndMs: block.active ? activeBleedEndMs : undefined,
          });
          if (position == null) {
            return null;
          }
          const shortLabel = formatBlockLabel(block);
          const scanSuffix =
            block.kind !== "misc" && block.deviceCount != null ? ` · ${block.deviceCount}` : "";
          const isSelectedMiscEdit =
            block.kind === "misc" &&
            miscCallout?.mode === "edit" &&
            miscCallout.miscEntryId === block.id;
          const blockInner = (
            <div
              className={cn(
                "flex h-full min-h-9 items-center overflow-hidden rounded border px-1 text-[10px] font-medium leading-tight shadow-sm",
                blockColorClass(block),
                block.active ? "text-slate-900" : "text-white",
                isSelectedMiscEdit && "ring-2 ring-amber-800 ring-offset-1",
              )}
            >
              <span className="truncate">
                {shortLabel}
                {scanSuffix}
                {block.active ? " · now" : ""}
              </span>
            </div>
          );
          return (
            <div
              key={`${block.kind}-${block.id}`}
              {...{ [DAILY_TASK_BLOCK_ATTRIBUTE]: `${block.kind}-${block.id}` }}
              {...(block.kind === "misc"
                ? { [DAILY_TASK_MISC_BLOCK_ATTRIBUTE]: String(block.id) }
                : {})}
              className="absolute top-1 bottom-1 z-[1] min-w-[2px]"
              style={{
                left: `${position.leftPct}%`,
                width: `${position.widthPct}%`,
              }}
            >
              {block.kind === "misc" && hubWritesAllowed ? (
                <button
                  type="button"
                  className="h-full w-full cursor-pointer rounded text-left"
                  onClick={(event) => {
                    event.stopPropagation();
                    onMiscBlockClick(block, employee);
                  }}
                >
                  {blockInner}
                </button>
              ) : (
                <HoverHintContent hoverHint={blockHint(block)} block cursor="default" className="h-full w-full">
                  {blockInner}
                </HoverHintContent>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Auto-refetch interval while viewing today's daily task report. */
export const DAILY_TASK_REPORT_AUTO_REFRESH_MS = 10_000;

export function DailyTaskReportTab({
  onViewingTodayChange,
  refreshSignal = 0,
}: {
  onViewingTodayChange?: (viewingToday: boolean) => void;
  refreshSignal?: number;
}) {
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const anchorKeyRef = useRef(0);
  const laneDragRef = useRef<ActiveLaneDrag | null>(null);
  const laneRectRef = useRef<DOMRect | null>(null);
  const [miscCallout, setMiscCallout] = useState<MiscCalloutState | null>(null);
  const [dragPreview, setDragPreview] = useState<{ userId: string; startMs: number; endMs: number } | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: LABOR_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const viewingToday =
    selectedDate ===
    new Intl.DateTimeFormat("en-CA", {
      timeZone: LABOR_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());

  useEffect(() => {
    if (!viewingToday) {
      return;
    }
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => setNowMs(Date.now()), DAILY_TASK_REPORT_AUTO_REFRESH_MS);
    return () => window.clearInterval(intervalId);
  }, [viewingToday]);

  useEffect(() => {
    onViewingTodayChange?.(viewingToday);
  }, [onViewingTodayChange, viewingToday]);

  const dailyTaskQuery = useQuery({
    queryKey: ["manufacturing-reporting-daily-task", selectedDate],
    queryFn: () =>
      apiFetch<DailyTaskReportResponse>("/api/manufacturing/reporting/daily-task", {
        query: { date: selectedDate, background: "1" },
      }),
    staleTime: viewingToday ? DAILY_TASK_REPORT_AUTO_REFRESH_MS : 60_000,
  });

  useHubCacheStream({
    channels: [HUB_CACHE_CHANNEL.manufacturingReportingDailyTask(selectedDate)],
    onEvent: (event) => {
      if (event.phase === "ready" || event.phase === "invalidated") {
        void queryClient.invalidateQueries({ queryKey: ["manufacturing-reporting-daily-task", selectedDate] });
      }
    },
  });

  useEffect(() => {
    if (refreshSignal <= 0) {
      return;
    }
    void queryClient.fetchQuery({
      queryKey: ["manufacturing-reporting-daily-task", selectedDate],
      queryFn: () =>
        apiFetch<DailyTaskReportResponse>("/api/manufacturing/reporting/daily-task", {
          query: { date: selectedDate, refresh: "1" },
        }),
    });
  }, [queryClient, refreshSignal, selectedDate]);

  const quickbooksTimeOAuthQuery = useQuery({
    queryKey: ["manufacturing-reporting", "quickbooks-time-oauth-status"],
    queryFn: fetchQuickBooksTimeOAuthStatus,
    staleTime: 30_000,
  });

  const quickbooksTimeConnectMutation = useMutation({
    mutationFn: () =>
      startQuickBooksTimeOAuth({
        returnTo: `${location.pathname}${location.search}${location.hash}`,
        uiOrigin: window.location.origin,
      }),
    onSuccess: (payload) => {
      window.location.assign(payload.authorizeUrl);
    },
  });

  const quickbooksTimeOperatorEligible = quickbooksTimeOAuthQuery.data?.operatorEligible === true;
  const quickbooksTimeReconnectNeeded = quickBooksTimeNeedsReconnect(quickbooksTimeOAuthQuery.data);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get("quickbooksTime");
    if (outcome === "connected" || outcome === "error") {
      params.delete("quickbooksTime");
      params.delete("quickbooksTimeError");
      const nextSearch = params.toString();
      void navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
        { replace: true },
      );
      void queryClient.invalidateQueries({ queryKey: ["manufacturing-reporting", "quickbooks-time-oauth-status"] });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing-reporting-daily-task"] });
    }
  }, [location.pathname, location.search, navigate, queryClient]);

  const employees = dailyTaskQuery.data?.employees ?? [];
  const dayStartIso = dailyTaskQuery.data?.dayStart ?? new Date(`${selectedDate}T00:00:00Z`).toISOString();
  const dateTitle = dateTitleFormatter.format(new Date(`${selectedDate}T12:00:00`));

  const timelineHours = useMemo(
    () =>
      computeTimelineHours(employees, dayStartIso, viewingToday ? { includeNowMs: nowMs } : undefined),
    [employees, dayStartIso, nowMs, viewingToday],
  );
  const timelineWindow = useMemo(
    () => timelineWindowFromHours(dayStartIso, timelineHours.startHour, timelineHours.endHour),
    [dayStartIso, timelineHours.endHour, timelineHours.startHour],
  );
  const dayStartMs = new Date(dayStartIso).getTime();

  const finalizeMiscDraft = useCallback(
    (drag: ActiveLaneDrag, endMs: number) => {
      let startMs = drag.startMs;
      let resolvedEndMs = endMs;
      if (!drag.moved) {
        resolvedEndMs = startMs + CLICK_DURATION_MS;
      } else {
        if (resolvedEndMs < startMs) {
          const swap = startMs;
          startMs = resolvedEndMs;
          resolvedEndMs = swap;
        }
        if (resolvedEndMs - startMs < MIN_DRAG_DURATION_MS) {
          resolvedEndMs = startMs + MIN_DRAG_DURATION_MS;
        }
      }
      resolvedEndMs = Math.min(resolvedEndMs, timelineWindow.windowEndMs);
      startMs = Math.max(startMs, timelineWindow.windowStartMs);
      if (resolvedEndMs <= startMs) {
        return;
      }
      const position = rangePosition(startMs, resolvedEndMs, timelineWindow);
      anchorKeyRef.current += 1;
      setMiscCallout({
        mode: "create",
        anchorKey: String(anchorKeyRef.current),
        userId: drag.userId,
        employeeLabel: drag.employeeLabel,
        startTime: msToChicagoTimeString(startMs),
        endTime: msToChicagoTimeString(resolvedEndMs),
        leftPct: position.leftPct,
        widthPct: position.widthPct,
      });
    },
    [timelineWindow],
  );

  const handleLanePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, employee: DailyTaskEmployee) => {
      setMiscCallout(null);
      const laneRect = event.currentTarget.getBoundingClientRect();
      laneRectRef.current = laneRect;
      const startMs = pointerXToMs(event.clientX, laneRect, timelineWindow, dayStartMs);
      laneDragRef.current = {
        userId: employee.userId,
        employeeLabel: employee.label,
        pointerId: event.pointerId,
        startMs,
        currentMs: startMs,
        startClientX: event.clientX,
        moved: false,
      };
      setDragPreview({ userId: employee.userId, startMs, endMs: startMs + CLICK_DURATION_MS });
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [dayStartMs, timelineWindow],
  );

  const handleLanePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = laneDragRef.current;
      if (drag == null || drag.pointerId !== event.pointerId || laneRectRef.current == null) {
        return;
      }
      const currentMs = pointerXToMs(event.clientX, laneRectRef.current, timelineWindow, dayStartMs);
      if (Math.abs(event.clientX - drag.startClientX) > MIN_DRAG_DISTANCE_PX) {
        drag.moved = true;
      }
      drag.currentMs = currentMs;
      const endMs = drag.moved ? currentMs : drag.startMs + CLICK_DURATION_MS;
      const rangeStart = drag.moved ? Math.min(drag.startMs, endMs) : drag.startMs;
      const rangeEnd = drag.moved ? Math.max(drag.startMs + MIN_DRAG_DURATION_MS, endMs) : drag.startMs + CLICK_DURATION_MS;
      setDragPreview({
        userId: drag.userId,
        startMs: rangeStart,
        endMs: rangeEnd,
      });
    },
    [dayStartMs, timelineWindow],
  );

  const handleLanePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const drag = laneDragRef.current;
      if (drag == null || drag.pointerId !== event.pointerId) {
        return;
      }
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      finalizeMiscDraft(drag, drag.currentMs);
      laneDragRef.current = null;
      laneRectRef.current = null;
      setDragPreview(null);
    },
    [finalizeMiscDraft],
  );

  const handleMiscBlockClick = useCallback((block: DailyTaskBlock, employee: DailyTaskEmployee) => {
    if (block.kind !== "misc") {
      return;
    }
    setMiscCallout({
      mode: "edit",
      miscEntryId: block.id,
      userId: employee.userId,
      employeeLabel: employee.label,
      startTime: msToChicagoTimeString(new Date(block.startedAt).getTime()),
      endTime: msToChicagoTimeString(new Date(block.endedAt).getTime()),
      description: block.label,
      createdByLabel: block.createdByLabel,
    });
  }, []);

  const dailyTaskSectionHelp = useMemo(
    () =>
      hubWritesAllowed
        ? `One calendar day per operator with hub station clock activity in the last 14 days (${LABOR_TIME_ZONE}). Station colors are hub labor. Gray hatched bands are QuickBooks Time off-the-clock time (before clock in, after clock out, and between work segments). Lighter hatched bands are logged breaks. Green and red markers are clock in and clock out. Click or drag on an empty lane to add misc time. Click a misc block to edit or delete.`
        : `One calendar day per operator with hub station clock activity in the last 14 days (${LABOR_TIME_ZONE}). Station colors are hub labor. Gray hatched bands are QuickBooks Time off-the-clock time (before clock in, after clock out, and between work segments). Lighter hatched bands are logged breaks. Green and red markers are clock in and clock out. Misc time editing requires write access.`,
    [hubWritesAllowed],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CardTitleWithHelp hoverHint={dailyTaskSectionHelp} titleClassName="text-lg font-semibold">
          Daily task
        </CardTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            aria-label="Previous day"
            onClick={() => {
              setMiscCallout(null);
              setSelectedDate((current) => shiftCalendarDate(current, -1));
            }}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Button>
          <input
            type="date"
            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            value={selectedDate}
            onChange={(event) => {
              if (event.target.value !== "") {
                setMiscCallout(null);
                setSelectedDate(event.target.value);
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            aria-label="Next day"
            onClick={() => {
              setMiscCallout(null);
              setSelectedDate((current) => shiftCalendarDate(current, 1));
            }}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>

      <p className="text-sm font-medium text-slate-800">{dateTitle}</p>

      {dailyTaskQuery.data?.quickbooksTimeStatus === "not_configured" &&
      !quickbooksTimeOperatorEligible ? (
        <p className="text-sm text-amber-800">
          QuickBooks Time is not connected — clock in, clock out, and break overlays are hidden. Station labor below
          still loads.
        </p>
      ) : null}
      {dailyTaskQuery.data?.quickbooksTimeStatus === "not_configured" &&
      quickbooksTimeOperatorEligible &&
      !quickbooksTimeOAuthQuery.data?.clientConfigured ? (
        <p className="text-sm text-amber-800">
          QuickBooks Time sign-in is not configured on this server. Station labor below still loads.
        </p>
      ) : null}
      {dailyTaskQuery.data?.quickbooksTimeStatus === "not_configured" &&
      quickbooksTimeOperatorEligible &&
      quickbooksTimeOAuthQuery.data?.clientConfigured ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          <p>
            QuickBooks Time is not connected — clock in, clock out, and break overlays are hidden. Station labor below
            still loads.
          </p>
          <button
            type="button"
            className="mt-2 font-medium text-amber-900 underline decoration-amber-500 underline-offset-2 hover:text-amber-950"
            disabled={quickbooksTimeConnectMutation.isPending}
            onClick={() => void quickbooksTimeConnectMutation.mutate()}
          >
            Connect QuickBooks Time
          </button>
        </div>
      ) : null}
      {dailyTaskQuery.data?.quickbooksTimeStatus === "error" ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>
            {friendlyQuickBooksTimeLoadError(
              dailyTaskQuery.data.quickbooksTimeError,
              quickbooksTimeOperatorEligible,
            )}
          </p>
          {quickbooksTimeOperatorEligible && quickbooksTimeReconnectNeeded ? (
            <button
              type="button"
              className="mt-2 font-medium text-red-900 underline decoration-red-400 underline-offset-2 hover:text-red-950"
              disabled={quickbooksTimeConnectMutation.isPending}
              onClick={() => void quickbooksTimeConnectMutation.mutate()}
            >
              Reconnect QuickBooks Time
            </button>
          ) : null}
        </div>
      ) : null}

      {dailyTaskQuery.isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>
            {friendlyDailyTaskLoadError(
              dailyTaskQuery.error instanceof ApiError ? dailyTaskQuery.error : dailyTaskQuery.error,
              quickbooksTimeOperatorEligible,
            )}
          </p>
          {quickbooksTimeOperatorEligible && quickbooksTimeReconnectNeeded ? (
            <button
              type="button"
              className="mt-2 font-medium text-red-900 underline decoration-red-400 underline-offset-2 hover:text-red-950"
              disabled={quickbooksTimeConnectMutation.isPending}
              onClick={() => void quickbooksTimeConnectMutation.mutate()}
            >
              Reconnect QuickBooks Time
            </button>
          ) : null}
        </div>
      ) : dailyTaskQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading daily task timeline…</p>
      ) : (
        <Card>
          <CardBody className="space-y-1">
            <TimelineHourLabels dayStartIso={dayStartIso} window={timelineWindow} />
            <div className="relative">
              {viewingToday ? (
                <div className="pointer-events-none absolute inset-x-0 top-0 bottom-0 z-[4] flex gap-3">
                  <div className="w-24 shrink-0" aria-hidden />
                  <div className="relative min-w-0 flex-1">
                    <TimelineCurrentTimeMarker window={timelineWindow} nowMs={nowMs} />
                  </div>
                </div>
              ) : null}
            {employees.map((employee) => (
              <EmployeeTimelineRow
                key={employee.key}
                employee={employee}
                window={timelineWindow}
                hubWritesAllowed={hubWritesAllowed}
                miscCallout={miscCallout}
                dragPreview={dragPreview}
                nowMs={nowMs}
                viewingToday={viewingToday}
                onLanePointerDown={handleLanePointerDown}
                onLanePointerMove={handleLanePointerMove}
                onLanePointerUp={handleLanePointerUp}
                onMiscBlockClick={handleMiscBlockClick}
              />
            ))}
            </div>
            <StationColorLegend />
          </CardBody>
        </Card>
      )}

      {miscCallout != null && hubWritesAllowed ? (
        <MiscEntryCallout
          state={miscCallout}
          date={selectedDate}
          employees={employees}
          onClose={() => setMiscCallout(null)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ["manufacturing-reporting-daily-task", selectedDate] });
          }}
        />
      ) : null}
    </div>
  );
}
